import classNames from 'classnames';
import { useMemo } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Splide, SplideTrack, SplideSlide } from '@splidejs/react-splide';

import { isMobile, SIZE_SM, SIZE_XS, SIZE_MD } from '../../../../hooks/useBreakPoint';
import { SLIDER_TRACK_PADDING } from '../../config/config';

// eslint-disable-next-line import/no-unresolved
import '@splidejs/react-splide/css';
import './SliderV3.scss';

const getPerPage = (slidesPerPage, mobileType) => {
  const validSlidesPerPage = slidesPerPage || 1;

  switch (mobileType) {
    case SIZE_XS:
    case SIZE_SM: {
      const halfSlides = Math.floor(validSlidesPerPage / 2);
      return validSlidesPerPage > 2 ? (halfSlides || 1) : validSlidesPerPage;
    }
    default:
      return validSlidesPerPage;
  }
};

const SliderV3 = ({
  id = '',
  slides = [],
  slidesPerPage = 6,
  type = 'slide',
  autoplay = false,
  interval = 8000,
  rewind,
  gap = '1rem',
  mobileGap,
  fixedWidth,
  backgroundColor,
  trackPadding = SLIDER_TRACK_PADDING.SMALL,
  advancedMode = false,
  breakpoints = {
    1200: {
      perPage: 5,
    },
    992: {
      perPage: 4,
    },
    768: {
      perPage: 3,
    },
    576: {
      perPage: 2,
    },
  },
}) => {
  const mobileSM = isMobile([SIZE_SM, SIZE_XS]) && SIZE_SM;
  const mobileMD = isMobile([SIZE_MD]) && SIZE_MD;

  const {
    mobileType,
    isOverflow,
    finalPerPage,
    needsAdvancedBehavior,
  } = useMemo(() => {
    const mobileTypeValue = mobileSM || mobileMD || false;

    return {
      mobileType: mobileTypeValue,
      isOverflow: advancedMode,
      needsAdvancedBehavior: advancedMode,
      finalPerPage: advancedMode
        ? getPerPage(slidesPerPage, mobileSM)
        : slidesPerPage,
    };
  }, [mobileSM, mobileMD, slidesPerPage, advancedMode, fixedWidth]);

  const splideOptions = useMemo(() => {
    const baseOptions = {
      type,
      pagination: false,
      arrows: true,
      autoplay,
      interval,
      rewind,
      gap: (mobileType && mobileGap) ? mobileGap : gap,
      perPage: finalPerPage,
      perMove: 1,
      fixedWidth,
      breakpoints,
    };

    const advancedOptions = {};

    if (needsAdvancedBehavior && !fixedWidth
      && (finalPerPage > 1 && mobileType)) {
      advancedOptions.omitEnd = true;
      advancedOptions.focus = 0;
      advancedOptions.fixedWidth = `calc(((100% + 1rem) / ${finalPerPage}) - ${isOverflow ? '2' : '1'}rem)`;
    }

    return {
      ...baseOptions,
      ...advancedOptions,
    };
  }, [
    type, autoplay, interval, rewind, mobileType, gap, mobileGap, finalPerPage, fixedWidth,
    breakpoints, isOverflow, needsAdvancedBehavior,
  ]);

  return (
    <div
      className="SliderV3"
      data-advanced-responsive={needsAdvancedBehavior}
      style={backgroundColor ? {
        '--slider-bg-color': backgroundColor,
      } : undefined}
    >
      <Splide
        id={`sliderV3-${id}`}
        className="SliderV3__slider"
        hasTrack={false}
        options={splideOptions}
        aria-labelledby="slider"
      >
        <SplideTrack
          className={classNames(trackPadding, { calculatedWidth: !fixedWidth })}
        >
          {slides.map(({ key, slide }) => (
            <SplideSlide key={key}>
              {slide}
            </SplideSlide>
          ))}
        </SplideTrack>
        <div className="splide__arrows">
          <button type="button" className="splide__arrow splide__arrow--prev" aria-label="Prev" />
          <button type="button" className="splide__arrow splide__arrow--next" aria-label="Next" />
        </div>
      </Splide>
    </div>
  );
};

export default SliderV3;
